import React, { useCallback } from 'react';
import {
	CUSTOM_ITEM_BUTTON,
	CUSTOM_ITEM_ACTION,
} from '@jira/navigation-apps__sidebar-nav4-analytics/src/common/constants/analytics/component-type.tsx';
import { APP } from '@jira/navigation-apps__sidebar-nav4-analytics/src/common/constants/analytics/item-type.tsx';
import {
	LEVEL_THREE,
	LEVEL_TWO,
} from '@jira/navigation-apps__sidebar-nav4-analytics/src/common/constants/analytics/level.tsx';
import { MENU_ID_ANALYTIC_EVENT_IDS } from '@jira/navigation-apps__sidebar-nav4-analytics/src/common/constants/analytics/side-nav.tsx';
import { MENU_ID_APPS } from '@jira/navigation-apps__sidebar-nav4-context/src/common/constants.tsx';
import type { OnCloseProps } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/common/types.tsx';
import { MoreNavMenuButton } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/ui/more-nav-menu-button/main.tsx';
import type { MoreNavMenuButtonMenuItemsProps } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/ui/more-nav-menu-button/submenu/index.tsx';
import { token } from '@atlaskit/tokens';
import Section from '@atlaskit/menu/section';
import LinkItem from '@atlaskit/menu/link-item';
import SettingsIcon from '@atlaskit/icon/core/settings';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { useIntl, defineMessage } from '@atlassian/jira-intl';
import { expVal } from '@atlassian/jira-feature-experiments';

const appSettings = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-content-apps.common.app-actions.app-settings',

	defaultMessage: 'App settings',
	description:
		'Link to the admin page of the app. It is shown in the dropdown under the Apps menu on a global sidebar and is triggered per app',
});

type AppActionsProps = {
	settingsUrl: string;
	appId: string;
	reorderActions?: (onClose: () => void) => React.ReactNode;
} & OnCloseProps;

type NavMenuItemsProps = MoreNavMenuButtonMenuItemsProps & {
	settingsUrl?: string;
	onTrackClick: () => void;
	reorderActions?: (onClose: () => void) => React.ReactNode;
};

const NavMenuItems = ({
	onClose,
	onTrackClick,
	settingsUrl,
	reorderActions,
}: NavMenuItemsProps) => {
	const { formatMessage } = useIntl();

	const handleClick = useCallback(
		(event: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>) => {
			onTrackClick();
			onClose?.(event);
		},
		[onClose, onTrackClick],
	);

	const handleSimpleClose = useCallback(() => {
		onClose?.(null);
	}, [onClose]);

	if (expVal('navx-2365-add_dnd_to_jira_l2_menu_items', 'isEnabled', false)) {
		return (
			<Section>
				{settingsUrl && (
					<LinkItem
						iconBefore={<SettingsIcon label="" color={token('color.icon')} />}
						href={settingsUrl}
						target="_blank"
						onClick={handleClick}
					>
						{formatMessage(appSettings)}
					</LinkItem>
				)}
				{reorderActions?.(handleSimpleClose)}
			</Section>
		);
	}

	return (
		<Section>
			<LinkItem
				iconBefore={<SettingsIcon label="" color={token('color.icon')} />}
				href={settingsUrl}
				target="_blank"
				onClick={handleClick}
			>
				{formatMessage(appSettings)}
			</LinkItem>
		</Section>
	);
};

export function AppActions({
	settingsUrl,
	appId,
	onClose,
	reorderActions,
}: AppActionsProps): React.JSX.Element | null {
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const fireOpenEvent = useCallback(() => {
		fireUIAnalytics(createAnalyticsEvent({}), {
			action: 'clicked',
			actionSubject: 'button',
			actionSubjectId: MENU_ID_ANALYTIC_EVENT_IDS[MENU_ID_APPS],
			attributes: {
				level: LEVEL_TWO,
				componentType: CUSTOM_ITEM_BUTTON,
				itemId: 'meatballDropdown',
				section: 'yourApps',
			},
		});
	}, [createAnalyticsEvent]);

	const fireClickEvent = useCallback(() => {
		fireUIAnalytics(createAnalyticsEvent({}), {
			action: 'clicked',
			actionSubject: 'dropdownItem',
			actionSubjectId: MENU_ID_ANALYTIC_EVENT_IDS[MENU_ID_APPS],
			attributes: {
				level: LEVEL_THREE,
				componentType: CUSTOM_ITEM_ACTION,
				parentItemId: appId,
				parentItemType: APP,
				itemId: 'appSettings',
				section: 'yourApps',
			},
		});
	}, [createAnalyticsEvent, appId]);

	const MenuItems = useCallback(
		(props: MoreNavMenuButtonMenuItemsProps) => (
			<NavMenuItems
				onClose={props.onClose}
				onTrackClick={fireClickEvent}
				settingsUrl={settingsUrl}
				reorderActions={reorderActions}
			/>
		),
		[fireClickEvent, settingsUrl, reorderActions],
	);

	if (expVal('navx-2365-add_dnd_to_jira_l2_menu_items', 'isEnabled', false)) {
		if (!settingsUrl && !reorderActions) {
			return null;
		}

		return <MoreNavMenuButton MenuItems={MenuItems} onOpen={fireOpenEvent} onClose={onClose} />;
	}

	if (!settingsUrl) {
		return null;
	}

	return <MoreNavMenuButton MenuItems={MenuItems} onOpen={fireOpenEvent} onClose={onClose} />;
}
